// src/components/HeaderNav.tsx
import { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FullLogo } from './Logo';
import { ForceReloadLink } from './ForceReloadLink.tsx';

const calculatorLinks = [
  { name: 'Home Affordability', href: '/home-affordability-calculator' },
  { name: 'Mortgage Calculator', href: '/mortgage-payment-calculator' },
  { name: 'Future Purchase Date', href: '/purchase-timeline-calculator' },
  { name: 'Down Payment Savings', href: '/down-payment-calculator' }
];

const embedLinks = [
  { name: 'Home Affordability Embed', href: '/home-affordability-embed' },
  { name: 'Mortgage Payment Embed', href: '/mortgage-payment-embed' },
  { name: 'Future Purchase Embed', href: '/purchase-timeline-embed' },
  { name: 'Down Payment Embed', href: '/down-payment-embed' }
];

export function HeaderNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isEmbedMenuOpen, setIsEmbedMenuOpen] = useState(false);
  const embedMenuRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const checkMobile = () => {
      // Changed breakpoint from 768 to 1024 to switch to mobile mode earlier
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  useEffect(() => {
    // Close the embed menu when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (embedMenuRef.current && !embedMenuRef.current.contains(event.target as Node)) {
        setIsEmbedMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const toggleEmbedMenu = () => {
    setIsEmbedMenuOpen(!isEmbedMenuOpen);
  };
  
  // New hover styles for nav links - similar to the embed section links
  const navLinkClass = "relative px-3 py-2 font-bold text-gray-700 transition-colors";
  const navLinkHoverClass = "hover:text-primary";
  
  // Updated active indicator style - fade in effect instead of sliding from the right
  const activeIndicatorClass = "after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:opacity-0 after:transition-opacity after:duration-300 hover:after:opacity-100";
  
  return (
    <header className="bg-white border-b border-gray-200 h-20 w-full">
      <div className="max-w-[1280px] mx-auto h-full">
        <div className="w-full flex items-center justify-between px-4 h-full">
          {/* Logo on left */}
          <ForceReloadLink to="/" className="flex items-center">
            <FullLogo width={26} height={26} />
            <span className="ml-2 font-medium text-lg text-gray-900">BuyHomeCalc</span>
          </ForceReloadLink>
          
          {/* Desktop Nav Links - Changed from md:flex to xl:flex */}
          <div className="hidden xl:flex items-center space-x-4">
            {calculatorLinks.map((item) => (
              <ForceReloadLink
                key={item.name}
                to={item.href}
                className={`${navLinkClass} ${navLinkHoverClass} ${activeIndicatorClass}`}>
                {item.name}
              </ForceReloadLink>
            ))}
            
            {/* Desktop Embed Dropdown */}
            <div className="relative" ref={embedMenuRef}>
              <button
                onClick={toggleEmbedMenu}
                className={`${navLinkClass} ${navLinkHoverClass} ${activeIndicatorClass} flex items-center gap-1`}
                aria-expanded={isEmbedMenuOpen}
                aria-controls="desktop-embed-submenu"
              >
                Embed Tools
                {isEmbedMenuOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              
              {/* Dropdown menu for embed pages */}
              {isEmbedMenuOpen && (
                <div 
                  id="desktop-embed-submenu"
                  className="absolute right-0 mt-2 w-52 bg-white rounded-md shadow-lg border border-gray-200 z-50"
                >
                  <div className="py-1">
                    {embedLinks.map((item) => (
                      <ForceReloadLink
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsEmbedMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary">
                        {item.name}
                      </ForceReloadLink>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
      
          {/* Hamburger menu - Changed from md:hidden to xl:hidden */}
          <button
            onClick={toggleMenu}
            className="xl:hidden p-2 rounded-lg text-gray-900 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
          
          {/* Mobile menu - SIMPLIFIED VERSION */}
          {isMobile && (
            <>
              <div
                className={cn(
                  'fixed top-[80px] right-0 bottom-0 w-64 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto',
                  isMenuOpen ? 'translate-x-0' : 'translate-x-full'
                )}
              >
                <div className="flex flex-col p-4">
                  {/* Basic calculator links */}
                  {calculatorLinks.map((item) => (
                    <ForceReloadLink
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="px-4 py-3 rounded-lg text-base font-bold text-gray-700 hover:text-primary hover:bg-gray-50 transition-colors mb-2">
                      {item.name}
                    </ForceReloadLink>
                  ))}
                  
                  {/* Mobile Embed Links - flat list instead of accordion */}
                  <div className="px-4 py-2 mb-1 border-t border-gray-100 pt-4">
                    <div className="text-base font-bold text-gray-700 mb-2">Embed Tools</div>
                    
                    {embedLinks.map((item) => (
                      <ForceReloadLink
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className="block py-2 text-sm text-gray-700 hover:text-primary transition-colors mb-1">
                        {item.name}
                      </ForceReloadLink>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Overlay */}
              {isMenuOpen && (
                <div 
                  className="fixed inset-0 bg-black/20 z-40"
                  onClick={() => setIsMenuOpen(false)}
                />
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}